import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { sessions } from '../lib/api';
import Layout from '../components/Layout';

export default function BlueprintView() {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);

  // セッション詳細を取得
  const { data: session } = useQuery({
    queryKey: ['session', id],
    queryFn: () => sessions.get(id!),
    enabled: !!id,
  });

  // 設計書を取得または生成
  const { data: blueprint, isLoading, error, refetch } = useQuery({
    queryKey: ['blueprint', id],
    queryFn: async () => {
      try {
        // まず既存の設計書を取得
        const response = await fetch(
          `${import.meta.env.DEV ? 'http://localhost:8787' : 'https://reactor-jarvis-app.valleyintest.workers.dev'}/api/sessions/${id}/blueprint`,
          { credentials: 'include' }
        );
        if (response.ok) {
          return response.json();
        }
        // 無い場合はnullを返す
        return null;
      } catch {
        return null;
      }
    },
    enabled: !!id,
  });

  // 設計書生成
  const generateMutation = useMutation({
    mutationFn: () => sessions.generateBlueprint(id!),
    onSuccess: () => {
      refetch();
    },
  });

  const handleCopyMarkdown = () => {
    if (blueprint?.markdownContent) {
      navigator.clipboard.writeText(blueprint.markdownContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadMarkdown = () => {
    if (blueprint?.markdownContent) {
      const blob = new Blob([blueprint.markdownContent], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${blueprint.title || 'blueprint'}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to={`/sessions/${id}`} className="text-jarvis-gold hover:underline">
                ← セッションに戻る
              </Link>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              設計書 (Blueprint)
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              {session?.initialIdea?.substring(0, 100) || '（未入力）'}
            </p>
          </div>
          {blueprint && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyMarkdown}
                className="btn-outline flex items-center gap-2"
              >
                {copied ? '✓ コピー済み' : '📋 コピー'}
              </button>
              <button
                onClick={handleDownloadMarkdown}
                className="btn-primary flex items-center gap-2"
              >
                📥 ダウンロード
              </button>
            </div>
          )}
        </div>

        {/* 設計書がない場合 */}
        {!blueprint && !error && (
          <div className="card text-center py-12">
            <p className="text-4xl mb-4">📝</p>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              設計書がまだ生成されていません
            </h2>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              ヒアリング内容を元に設計書を生成しますか？
            </p>
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="btn-primary text-lg px-8 py-3"
            >
              {generateMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  生成中...
                </span>
              ) : (
                '設計書を生成する'
              )}
            </button>
          </div>
        )}

        {/* 設計書表示 */}
        {blueprint && (
          <div className="card">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <div
                className="markdown-content"
                style={{ color: 'var(--text-primary)' }}
              >
                {/* シンプルなマークダウン表示（実際のプロジェクトではreact-markdownを使用） */}
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {blueprint.markdownContent}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* エラー */}
        {error && (
          <div className="card bg-red-500/10 border-red-500/30">
            <p className="text-red-500">設計書の取得に失敗しました</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

